// Onboarding (app flow §2), one question per screen, each step turning in from the direction of
// travel (Steps). Handle → name and age → photo → Fellowship on? → how you like to read → a plan to
// start → friends → a first verse. Back works, every optional step can be skipped, and answers are
// kept on this device so leaving mid-way resumes where the reader stopped. The age itself is never
// stored or sent: only "13 or over" (PRD A9). The last step asks for the walkthrough on Home.
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useDeferredValue, useEffect, useRef, useState } from "react";
import { AuthFrame } from "../components/AuthFrame";
import type { ArtName } from "../components/Art";
import { Avatar } from "../components/Avatar";
import { AvatarPicker } from "../components/AvatarPicker";
import { Button, LinkButton } from "../components/Button";
import { Icon } from "../components/Icon";
import { PlanCover } from "../components/PlanCover";
import { SegmentedControl } from "../components/Tabs";
import { showError, useToast } from "../components/Toast";
import ui from "../components/ui.module.css";
import { usePlans, useStartPlan } from "../data/planQueries";
import { localDate } from "../data/api";
import { keys, refreshSession, useCreatePost, useFriendAction, usePeople, useSession, useSource, useVotd } from "../data/queries";
import { useDraft } from "../lib/useDraft";
import { getReadingSize, setReadingSize, type ReadingSize } from "../lib/reading";
import { getThemeChoice, setThemeChoice, type ThemeChoice } from "../lib/theme";
import { requestTour } from "../lib/tour";
import { useTitle } from "../lib/useTitle";
import { Steps } from "../motion/Steps";
import a from "./auth.module.css";
import s from "./welcome.module.css";

const TOTAL = 8;
type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export const Route = createFileRoute("/welcome")({
  validateSearch: (q: Record<string, unknown>): { step?: Step } => {
    const n = Number(q.step);
    return n >= 1 && n <= TOTAL ? { step: n as Step } : {};
  },
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({ queryKey: keys.session, queryFn: () => context.source.session() });
    if (!session) throw redirect({ to: "/sign-in" });
  },
  component: Welcome,
});

/** What each step is called (for the progress line) and the plate beside it. */
const STEPS: Record<Step, { name: string; art: ArtName; focus?: string; verse: { text: string; ref: string } }> = {
  1: { name: "Your handle", art: "friedrich", verse: { text: "A friend loves at all times; and a brother is born for adversity.", ref: "Proverbs 17:17 · WEB" } },
  2: { name: "About you", art: "friedrich", verse: { text: "But I have called you friends, for everything that I heard from my Father, I have made known to you.", ref: "John 15:15 · WEB" } },
  3: { name: "Your photo", art: "friedrich", verse: { text: "Iron sharpens iron; so a man sharpens his friend’s countenance.", ref: "Proverbs 27:17 · WEB" } },
  4: { name: "Fellowship", art: "cole", verse: { text: "Let’s consider how to provoke one another to love and good works.", ref: "Hebrews 10:24 · WEB" } },
  5: { name: "Reading", art: "latour", focus: "50% 40%", verse: { text: "Your word is a lamp to my feet, and a light for my path.", ref: "Psalm 119:105 · WEB" } },
  6: { name: "A plan", art: "ruisdael", verse: { text: "His delight is in Yahweh’s law. On his law he meditates day and night.", ref: "Psalm 1:2 · WEB" } },
  7: { name: "Friends", art: "friedrich", verse: { text: "Two are better than one, because they have a good reward for their labor.", ref: "Ecclesiastes 4:9 · WEB" } },
  8: { name: "A first verse", art: "cole", verse: { text: "Let the word of Christ dwell in you richly.", ref: "Colossians 3:16 · WEB" } },
};

const HANDLE = /^[a-z0-9_]{3,20}$/;

function Welcome() {
  useTitle("Welcome");
  const { step = 1 } = Route.useSearch();
  const navigate = useNavigate();
  const source = useSource();
  const qc = useQueryClient();
  const { data: session } = useSession();
  const [answers, setAnswers, clearAnswers] = useDraft("welcome", { handle: "", name: "", over13: false });
  const [avatar, setAvatar] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The direction of travel, so a step turns in from the right going on and from the left going back.
  const [prev, setPrev] = useState<number>(step);
  const [direction, setDirection] = useState(1);
  if (prev !== step) {
    setDirection(step > prev ? 1 : -1);
    setPrev(step);
  }
  const go = (n: number) => void navigate({ to: "/welcome", search: { step: n as Step } });
  const fellowshipOn = session?.onboarded ? session.fellowshipEnabled !== false : true;
  const plate = STEPS[step];

  // FS-136 D31: an under-13 answer deletes the account at once, so no child's email is kept. The API
  // lets an unfinished account go without a fresh sign-in.
  const leaveUnder13 = async () => {
    setSaving(true);
    try {
      await source.deleteAccount();
    } catch {
      // Whatever the answer, sign out: an account that couldn't be deleted stays hidden and unfinished,
      // and the nightly purge removes it.
      await source.signOut().catch(() => undefined);
    }
    clearAnswers();
    await refreshSession(qc, source);
    qc.clear();
    setSaving(false);
    void navigate({ to: "/" });
  };

  const finishSetup = async (on: boolean) => {
    setSaving(true);
    try {
      await source.completeOnboarding({ handle: answers.handle, displayName: answers.name.trim(), ageConfirmed: answers.over13, fellowshipEnabled: on });
    } catch (e) {
      setSaving(false);
      showError((e as Error).message);
      return;
    }
    await refreshSession(qc, source);
    await qc.invalidateQueries();
    clearAnswers();
    setSaving(false);
    go(5);
  };

  return (
    <AuthFrame
      art={plate.art}
      focus={plate.focus}
      verse={plate.verse}
      top={
        <div className={s.progress}>
          {step > 1 && step !== 5 && (
            <button type="button" className={a.backLink} onClick={() => go(step - 1)}>
              <Icon name="back" size={18} />
              Back
            </button>
          )}
          <span className={s.spine} role="img" aria-label={`Step ${step} of ${TOTAL}`} title={plate.name}>
            {Array.from({ length: TOTAL }, (_, i) => (
              <i key={i} data-on={i < step || undefined} data-here={i === step - 1 || undefined} />
            ))}
          </span>
        </div>
      }
    >
      <Steps step={step} direction={direction}>
        {step === 1 && <HandleStep value={answers.handle} onChange={(handle) => setAnswers({ ...answers, handle })} onNext={() => go(2)} />}
        {step === 2 && (
          <AboutStep name={answers.name} over13={answers.over13} leaving={saving} onChange={(patch) => setAnswers({ ...answers, ...patch })} onNext={() => go(3)} onTooYoung={() => void leaveUnder13()} />
        )}
        {step === 3 && (
          <PhotoStep
            name={answers.name}
            handle={answers.handle}
            avatarUrl={avatar}
            onAvatar={(url) => {
              setAvatar(url);
              void source.setAvatar(url);
            }}
            onNext={() => go(4)}
          />
        )}
        {step === 4 && <FellowshipStep busy={saving} onChoose={(on) => void finishSetup(on)} />}
        {step === 5 && <ReadingStep onNext={() => go(6)} />}
        {step === 6 && <PlanStep onNext={() => (fellowshipOn ? go(7) : void navigate({ to: "/settings" }))} />}
        {step === 7 && <FriendsStep inviteCode={session?.inviteCode ?? session?.user.handle ?? null} onNext={() => go(8)} />}
        {step === 8 && <FirstPostStep />}
      </Steps>
    </AuthFrame>
  );
}

/* ── 1. Handle ─────────────────────────────────────────────────────────── */

function HandleStep({ value, onChange, onNext }: { value: string; onChange: (v: string) => void; onNext: () => void }) {
  const source = useSource();
  const handle = useDeferredValue(value.trim().toLowerCase().replace(/^@/, ""));
  const [available, setAvailable] = useState<boolean | null>(null);
  const valid = HANDLE.test(handle);

  useEffect(() => {
    let live = true;
    if (!valid) return;
    void source.handleAvailable(handle).then((ok) => live && setAvailable(ok));
    return () => {
      live = false;
      setAvailable(null);
    };
  }, [handle, valid, source]);

  const state = !value ? "empty" : !valid ? "invalid" : available === null ? "checking" : available ? "free" : "taken";
  const message =
    state === "empty"
      ? "3 to 20 letters, numbers or underscores."
      : state === "invalid"
        ? "3 to 20 letters, numbers or underscores."
        : state === "checking"
          ? "Checking…"
          : state === "free"
            ? `@${handle} is yours if you want it.`
            : `@${handle} is taken. Try another.`;

  return (
    <>
      <h1 className={a.question}>Choose your handle</h1>
      <p className={a.lede}>Friends find you by it. It’s the only thing about you that can be searched.</p>
      <form
        className={a.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && available) onNext();
        }}
      >
        <div>
          <label htmlFor="handle" className="visually-hidden">
            Handle
          </label>
          <div className={s.handleField} data-state={state}>
            <span className={s.at} aria-hidden="true">
              @
            </span>
            <input
              id="handle"
              className={`${ui.field} ${a.big}`}
              placeholder="yourname"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              value={value}
              onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9_@]/g, ""))}
              aria-describedby="handle-status"
              aria-invalid={state === "taken" || undefined}
            />
            <span className={s.status} aria-hidden="true">
              {state === "checking" && <span className={ui.spinner} />}
              {state === "free" && <Icon name="check" size={18} />}
              {state === "taken" && <Icon name="close" size={18} />}
            </span>
          </div>
          <p id="handle-status" className={s.availability} data-state={state} aria-live="polite">
            {message}
          </p>
        </div>
        <Button type="submit" variant="primary" size="lg" wide disabled={!valid || !available}>
          Continue
        </Button>
      </form>
    </>
  );
}

/* ── 2. Name and age ───────────────────────────────────────────────────── */

/** Whole years between a date of birth and today. */
function ageOn(d: number, m: number, y: number, today = new Date()): number | null {
  const dob = new Date(y, m - 1, d);
  if (dob.getFullYear() !== y || dob.getMonth() !== m - 1 || dob.getDate() !== d || dob > today || y < 1900) return null;
  let age = today.getFullYear() - y;
  if (today.getMonth() < m - 1 || (today.getMonth() === m - 1 && today.getDate() < d)) age -= 1;
  return age;
}

function AboutStep({
  name,
  over13,
  leaving,
  onChange,
  onNext,
  onTooYoung,
}: {
  name: string;
  over13: boolean;
  leaving: boolean;
  onChange: (p: { name?: string; over13?: boolean }) => void;
  onNext: () => void;
  onTooYoung: () => void;
}) {
  // The date itself stays in this component and is never stored or sent: only "13 or over" is.
  const [dob, setDob] = useState({ d: "", m: "", y: "" });
  const age = dob.d && dob.m && dob.y.length === 4 ? ageOn(Number(dob.d), Number(dob.m), Number(dob.y)) : null;
  const tooYoung = age !== null && age < 13;
  const ok = name.trim().length >= 1 && (over13 || (age !== null && age >= 13));
  const month = useRef<HTMLInputElement>(null);
  const year = useRef<HTMLInputElement>(null);
  const setPart = (part: "d" | "m" | "y", v: string) => {
    const clean = v.replace(/\D/g, "").slice(0, part === "y" ? 4 : 2);
    const next = { ...dob, [part]: clean };
    setDob(next);
    // Two digits of a day or month move on to the next box.
    if (part === "d" && clean.length === 2) month.current?.focus();
    if (part === "m" && clean.length === 2) year.current?.focus();
    const n = next.d && next.m && next.y.length === 4 ? ageOn(Number(next.d), Number(next.m), Number(next.y)) : null;
    onChange({ over13: n !== null && n >= 13 });
  };
  return (
    <>
      <h1 className={a.question}>What should friends call you?</h1>
      <p className={a.lede}>Your name as the people you read with know it.</p>
      <form
        className={a.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) onNext();
        }}
      >
        <div>
          <label htmlFor="name" className={ui.label}>
            Your name
          </label>
          <input id="name" className={`${ui.field} ${a.big}`} placeholder="Ruth Adeyemi" autoComplete="name" autoFocus maxLength={50} value={name} onChange={(e) => onChange({ name: e.target.value })} />
        </div>
        <fieldset className={s.dob} aria-describedby="dob-note">
          <legend className={ui.label}>Date of birth</legend>
          <label>
            <span className={s.dobLabel}>Day</span>
            <input className={`${ui.field} ${a.big}`} inputMode="numeric" autoComplete="bday-day" placeholder="DD" value={dob.d} onChange={(e) => setPart("d", e.target.value)} aria-invalid={tooYoung || undefined} />
          </label>
          <label>
            <span className={s.dobLabel}>Month</span>
            <input ref={month} className={`${ui.field} ${a.big}`} inputMode="numeric" autoComplete="bday-month" placeholder="MM" value={dob.m} onChange={(e) => setPart("m", e.target.value)} aria-invalid={tooYoung || undefined} />
          </label>
          <label>
            <span className={s.dobLabel}>Year</span>
            <input ref={year} className={`${ui.field} ${a.big}`} inputMode="numeric" autoComplete="bday-year" placeholder="YYYY" value={dob.y} onChange={(e) => setPart("y", e.target.value)} aria-invalid={tooYoung || undefined} />
          </label>
        </fieldset>
        <p id="dob-note" className={tooYoung ? `${ui.error} ${s.note}` : `${ui.hint} ${s.note}`} aria-live="polite">
          <Icon name={tooYoung ? "lock" : "shield"} size={16} />
          <span>
            {tooYoung
              ? "Fellowship is for people aged 13 and over, so we won’t keep an account for you. Bible Standard itself is yours to read."
              : "We only keep whether you’re 13 or over, never the date."}
          </span>
        </p>
        {tooYoung ? (
          <Button type="button" variant="secondary" size="lg" wide busy={leaving} onClick={onTooYoung}>
            Delete this account
          </Button>
        ) : (
          <Button type="submit" variant="primary" size="lg" wide disabled={!ok}>
            Continue
          </Button>
        )}
      </form>
    </>
  );
}

/* ── 3. Photo ──────────────────────────────────────────────────────────── */

function PhotoStep({ name, handle, avatarUrl, onAvatar, onNext }: { name: string; handle: string; avatarUrl: string | null; onAvatar: (url: string | null) => void; onNext: () => void }) {
  const display = name.trim() || "You";
  return (
    <>
      <h1 className={a.question}>Add a photo?</h1>
      <p className={a.lede}>It helps friends know it’s you. Your initials work just as well, and you can change it any time.</p>
      <div className={a.form}>
        {/* How you'll appear beside what you share. */}
        <div className={s.byline} aria-hidden="true">
          <Avatar user={{ displayName: display, avatarUrl }} size={44} />
          <span>
            <strong>{display}</strong> <span className={s.handle}>@{handle || "you"}</span>
            <span className={s.bylineQuote}>shared Psalm 46:1</span>
          </span>
        </div>
        <AvatarPicker user={{ displayName: display, avatarUrl }} onChange={onAvatar} />
        <Button variant="primary" size="lg" wide onClick={onNext}>
          Continue
        </Button>
        {!avatarUrl && (
          <button type="button" className={a.quiet} onClick={onNext}>
            Skip for now
          </button>
        )}
      </div>
    </>
  );
}

/* ── 4. Fellowship on? ─────────────────────────────────────────────────── */

function FellowshipStep({ busy, onChoose }: { busy: boolean; onChoose: (on: boolean) => void }) {
  return (
    <>
      <h1 className={a.question}>
        Turn on <em>Fellowship?</em>
      </h1>
      <p className={a.lede}>Your friends, and only your friends, will see what you share, and you’ll see theirs. Nothing is public.</p>
      <ul className={s.promises}>
        <li>
          <Icon name="people" size={20} />
          <span>Friends only, by request. No followers and no strangers.</span>
        </li>
        <li>
          <Icon name="shield" size={20} />
          <span>Report or block anyone in two taps. Every report is read by a person.</span>
        </li>
        <li>
          <Icon name="undo" size={20} />
          <span>Turn it off in Settings whenever you like. Nothing is lost.</span>
        </li>
      </ul>
      <div className={a.form}>
        <Button variant="primary" size="lg" wide busy={busy} disabled={busy} onClick={() => onChoose(true)} roll={false}>
          Turn on Fellowship
        </Button>
        <Button variant="secondary" size="lg" wide disabled={busy} onClick={() => onChoose(false)}>
          Not now, just read
        </Button>
      </div>
    </>
  );
}

/* ── 5. How you like to read ───────────────────────────────────────────── */

function ReadingStep({ onNext }: { onNext: () => void }) {
  const [size, setSize] = useState<ReadingSize>(getReadingSize);
  const [theme, setTheme] = useState<ThemeChoice>(getThemeChoice);
  return (
    <>
      <h1 className={a.question}>How do you like to read?</h1>
      <p className={a.lede}>Choose a size and a light. Scripture is quoted from the World English Bible; more translations are in the Bible Standard app.</p>
      <div className={a.form}>
        <figure className={s.specimen} aria-label="A preview of reading text">
          <blockquote>Your word is a lamp to my feet, and a light for my path.</blockquote>
          <figcaption>Psalm 119:105 · WEB</figcaption>
        </figure>
        <div className={s.choice}>
          <span className={s.choiceLabel} id="size-label">
            Text size
          </span>
          <SegmentedControl
            label="Text size"
            wide
            value={size}
            onChange={(v) => {
              setSize(v);
              setReadingSize(v);
            }}
            options={[
              { value: "regular", label: "Regular" },
              { value: "large", label: "Large" },
              { value: "larger", label: "Larger" },
            ]}
          />
        </div>
        <div className={s.choice}>
          <span className={s.choiceLabel}>Light</span>
          <SegmentedControl
            label="Light"
            wide
            value={theme}
            onChange={(v) => {
              setTheme(v);
              setThemeChoice(v);
            }}
            options={[
              { value: "light", label: "Day", icon: "sun" },
              { value: "dark", label: "Night", icon: "moon" },
              { value: "system", label: "Auto", icon: "device" },
            ]}
          />
        </div>
        <Button variant="primary" size="lg" wide onClick={onNext}>
          Continue
        </Button>
      </div>
    </>
  );
}

/* ── 6. A plan to start ────────────────────────────────────────────────── */

function PlanStep({ onNext }: { onNext: () => void }) {
  const { data: plans = [] } = usePlans();
  const start = useStartPlan();
  const toast = useToast();
  const [chosen, setChosen] = useState<string | null>(null);
  const offered = plans.filter((p) => p.visibility === "public" || p.classic).slice(0, 4);
  const plan = offered.find((p) => p.id === chosen);
  return (
    <>
      <h1 className={a.question}>Begin a reading plan?</h1>
      <p className={a.lede}>Read alone for now; you can invite friends to read with you later. Or skip, and find plans whenever you’re ready.</p>
      <div className={a.form}>
        <div className={s.plans} role="radiogroup" aria-label="Reading plans">
          {offered.map((p) => (
            <button key={p.id} type="button" role="radio" aria-checked={chosen === p.id} className={s.plan} onClick={() => setChosen(chosen === p.id ? null : p.id)}>
              <PlanCover plan={p} width={52} />
              <span className={s.planWords}>
                <span className={s.planTitle}>{p.title}</span>
                <span className={s.planMeta}>{p.scheduleKind === "calendar" ? "A reading for every date" : `${p.days} days`}</span>
              </span>
              <span className={s.tick} aria-hidden="true">
                <Icon name="check" size={16} />
              </span>
            </button>
          ))}
        </div>
        <Button
          variant="primary"
          size="lg"
          wide
          disabled={!plan || start.isPending}
          busy={start.isPending}
          roll={false}
          onClick={() =>
            plan &&
            start.mutate(
              { planId: plan.id, together: false, invite: [], startOn: localDate(), ...(plan.scheduleKind === "calendar" ? { follow: "byDate" as const } : {}) },
              {
                onSuccess: () => {
                  toast(`${plan.title} begins today.`);
                  onNext();
                },
              },
            )
          }
        >
          {plan ? `Start ${plan.title}` : "Choose a plan"}
        </Button>
        <button type="button" className={a.quiet} onClick={onNext}>
          Skip for now
        </button>
      </div>
    </>
  );
}

/* ── 7. Friends ────────────────────────────────────────────────────────── */

function FriendsStep({ inviteCode, onNext }: { inviteCode: string | null; onNext: () => void }) {
  const [q, setQ] = useState("");
  const query = useDeferredValue(q);
  const { data: people } = usePeople(query);
  const act = useFriendAction();
  const toast = useToast();
  const link = inviteCode ? `${location.origin}/i/${inviteCode}` : null;
  return (
    <>
      <h1 className={a.question}>Find your friends</h1>
      <p className={a.lede}>Search for the people you know by their handle, or send them your invitation.</p>
      <div className={a.form}>
        <div>
          <label htmlFor="find" className="visually-hidden">
            Search by handle
          </label>
          <div className={s.search}>
            <Icon name="search" size={18} />
            <input id="find" className={`${ui.field} ${a.big}`} placeholder="Search by handle, like @ruth" autoComplete="off" spellCheck={false} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
        {people && people.length > 0 && (
          <ul className={`${ui.list} ${s.people}`}>
            {people.map((u) => (
              <li key={u.id}>
                <span className={ui.person}>
                  <Avatar user={u} size={40} />
                  <span>
                    <span className={ui.personName}>{u.displayName}</span> <span className={ui.personHandle}>@{u.handle}</span>
                  </span>
                </span>
                {u.relation === "none" ? (
                  <Button size="sm" variant="secondary" icon="plus" onClick={() => act.mutate({ action: "request", userId: u.id, handle: u.handle })}>
                    Add
                  </Button>
                ) : (
                  <span className={s.sent}>
                    <Icon name="check" size={14} />
                    {u.relation === "friends" ? "Friends" : "Requested"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {link && (
          <div className={s.invite}>
            <span className={s.inviteWords}>
              <strong>Your invitation</strong>
              <span>{link.replace(/^https?:\/\//, "")}</span>
            </span>
            <Button
              size="sm"
              variant="secondary"
              icon="link"
              onClick={() => {
                const share = navigator.share?.bind(navigator);
                if (share && matchMedia("(pointer: coarse)").matches) void share({ title: "Read with me on Fellowship", url: link }).catch(() => undefined);
                else void navigator.clipboard?.writeText(link).then(() => toast("Invitation link copied."));
              }}
            >
              Copy link
            </Button>
          </div>
        )}
        <Button variant="primary" size="lg" wide onClick={onNext}>
          Continue
        </Button>
        <button type="button" className={a.quiet} onClick={onNext}>
          Skip for now
        </button>
      </div>
    </>
  );
}

/* ── 8. A first verse ──────────────────────────────────────────────────── */

function FirstPostStep() {
  const { data: votd } = useVotd();
  const create = useCreatePost();
  const navigate = useNavigate();
  const arrive = () => requestTour();
  return (
    <>
      <h1 className={a.question}>Share today’s verse?</h1>
      <p className={a.lede}>Your first post, if you like. Friends will see it on their Home.</p>
      {votd && (
        <figure className={s.verse}>
          <blockquote>{votd.quote}</blockquote>
          <figcaption>
            {votd.ref.label} · {votd.translation}
          </figcaption>
        </figure>
      )}
      <div className={a.form}>
        <Button
          variant="primary"
          size="lg"
          wide
          disabled={!votd || create.isPending}
          busy={create.isPending}
          roll={false}
          onClick={() =>
            create.mutate(
              { kind: "votd_repost", body: "", visibility: "friends" },
              {
                onSuccess: () => {
                  arrive();
                  void navigate({ to: "/home" });
                },
              },
            )
          }
        >
          Share it with friends
        </Button>
        <LinkButton to="/home" variant="secondary" size="lg" wide onClick={arrive}>
          Not today
        </LinkButton>
      </div>
    </>
  );
}
